import { TimePeriodCustom } from 'crono-fe-common/types/timePeriod';
import {
  differenceInSeconds,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isSameDay,
  isSameMonth,
  isSameYear,
  parseISO,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns';

export function newDateWithInitialTime() {
  return dateWithInitialTime(new Date());
}

export function dateWithInitialTime(date: Date) {
  const newDate = new Date(date);
  newDate.setHours(3, 0, 0, 0);
  return newDate;
}

export function newDateWithEndTime() {
  return dateWithEndTime(new Date());
}

export function dateWithEndTime(date: Date) {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
}

export function formatTime(time: Date) {
  return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function formatDateTime(time: Date) {
  return time.toLocaleString([], {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(time: Date) {
  return format(new Date(time), 'dd/MM/yyyy');
}

export function formatTodayDate() {
  const today = new Date();
  return (
    today.getFullYear() +
    '-' +
    (today.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    today.getDate().toString().padStart(2, '0')
  );
}

export function formatDateForParameter(date: Date) {
  const newDate = new Date(date);
  newDate.setUTCHours(0, 0, 0, 0);
  return newDate.toISOString();
}

export const getCurrentMonth = () => {
  const lastMonth = newDateWithInitialTime();
  lastMonth.setDate(1);

  const lastDay = newDateWithEndTime();
  lastDay.setDate(1);
  lastDay.setMonth(lastDay.getMonth() + 1);
  lastDay.setDate(lastDay.getDate() - 1);

  return {
    start: lastMonth,
    end: lastDay,
  };
};

export const getFirstDayOfNextMonth = () => {
  const currentDate = new Date();
  const nextMonth = new Date(currentDate);
  nextMonth.setMonth(currentDate.getMonth() + 1, 1);
  return nextMonth;
};

export const getCurrentWeek = () => {
  const lastMonday = newDateWithInitialTime();
  const thisFriday = newDateWithInitialTime();
  lastMonday.setDate(lastMonday.getDate() - lastMonday.getDay() + 1);
  thisFriday.setDate(thisFriday.getDate() - thisFriday.getDay() + 5);

  return { start: lastMonday, end: thisFriday };
};

export const getCurrentQuarter = () => {
  const lastQuarter = newDateWithInitialTime();
  lastQuarter.setMonth(Math.floor(lastQuarter.getMonth() / 3) * 3);
  lastQuarter.setDate(1);

  return { start: lastQuarter, end: new Date() };
};

type Range = { since: Date | null; to: Date | null };

const baseFactories: Record<TimePeriodCustom, () => Range> = {
  custom: () => ({ since: null, to: null }),
  today: () => ({ since: newDateWithInitialTime(), to: null }),

  'this-week': () => {
    const since = newDateWithInitialTime();
    since.setDate(since.getDate() - since.getDay() + 1);
    return { since, to: new Date() };
  },
  'this-month': () => {
    const since = newDateWithInitialTime();
    since.setDate(1);
    return { since, to: new Date() };
  },
  'this-quarter': () => {
    const since = newDateWithInitialTime();
    since.setMonth(Math.floor(since.getMonth() / 3) * 3, 1);
    return { since, to: new Date() };
  },
  'this-year': () => {
    const since = newDateWithInitialTime();
    since.setMonth(0, 1);
    return { since, to: new Date() };
  },

  'last-week': () => {
    const since = newDateWithInitialTime();
    since.setDate(since.getDate() - since.getDay() + 1 - 7);
    const to = newDateWithEndTime();
    to.setDate(to.getDate() - to.getDay());
    return { since, to };
  },
  'last-month': () => {
    const since = newDateWithInitialTime();
    since.setMonth(since.getMonth() - 1, 1);
    const to = newDateWithEndTime();
    to.setDate(0);
    return { since, to };
  },
  'last-quarter': () => {
    const since = newDateWithInitialTime();
    since.setMonth(Math.floor(since.getMonth() / 3) * 3 - 3, 1);
    const to = newDateWithEndTime();
    to.setMonth(Math.floor(to.getMonth() / 3) * 3, 0);
    return { since, to };
  },
  'last-year': () => {
    const since = newDateWithInitialTime();
    since.setFullYear(since.getFullYear() - 1, 0, 1);
    const to = newDateWithEndTime();
    to.setFullYear(to.getFullYear() - 1, 11, 31);
    return { since, to };
  },
};

const fullDateOverrides: Partial<Record<TimePeriodCustom, () => Date>> = {
  'this-week': () => endOfWeek(new Date()),
  'this-month': () => endOfMonth(new Date()),
  'this-quarter': () => endOfQuarter(new Date()),
  'this-year': () => endOfYear(new Date()),

  'last-quarter': () => endOfQuarter(subQuarters(new Date(), 1)),
  'last-month': () => endOfMonth(subMonths(new Date(), 1)),
  'last-year': () => endOfYear(subYears(new Date(), 1)),
};

export const getRangeFromTimePeriod = (
  value: TimePeriodCustom,
  fullDate = false,
): Range => {
  const { since, to: defaultTo } = baseFactories[value]();
  const to =
    fullDate && fullDateOverrides[value]
      ? fullDateOverrides[value]!()
      : defaultTo;
  return { since, to };
};

export const setToMidDayOfPassedDate = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(12, 0, 0, 0);
  return newDate;
};

export const convertSecondstoTime = (inputSeconds: number) => {
  const dateObj = new Date(inputSeconds * 1000);
  const hours = dateObj.getUTCHours();
  const minutes = dateObj.getUTCMinutes();
  const seconds = dateObj.getSeconds();

  const timeString =
    (hours.toString().padStart(2, '0') === '00'
      ? ''
      : hours.toString().padStart(2, '0') + ':') +
    minutes.toString().padStart(2, '0') +
    ':' +
    seconds.toString().padStart(2, '0');

  return timeString;
};

export const dateCompare = (a: Date, b: Date) => {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

export const isLessThanTomorrow = (a: Date) => {
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const tomorrowStart = new Date(
    todayStart.getFullYear(),
    todayStart.getMonth(),
    todayStart.getDate() + 1,
  );
  const aStart = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  return aStart < tomorrowStart;
};

export const isLessThanToday = (a: Date) => {
  return (
    a.getFullYear() === new Date().getFullYear() &&
    a.getMonth() === new Date().getMonth() &&
    a.getDate() < new Date().getDate()
  );
};

export const setTimeToDate = (baseDate: Date, newTime: Date): Date => {
  const updatedDate = new Date(baseDate);

  updatedDate.setHours(
    newTime.getHours(),
    newTime.getMinutes(),
    newTime.getSeconds(),
    newTime.getMilliseconds(),
  );

  return updatedDate;
};

/** Formats a Date's time as a 24h "HH:MM" string. */
export const dateToTimeString = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

/** Applies a 24h "HH:MM" time to baseDate, keeping its day and zeroing seconds/ms. */
export const setTimeStringToDate = (baseDate: Date, time: string): Date => {
  const [hours, minutes] = time.split(':').map(Number);
  const updatedDate = new Date(baseDate);
  updatedDate.setHours(hours, minutes, 0, 0);
  return updatedDate;
};

type TwoDigit = `${number}${number}`;
type TimeZoneOffset = `${'+' | '-'}${TwoDigit}:${TwoDigit}`;

function isTimeZoneOffset(str: string): str is TimeZoneOffset {
  return /^[+-]\d{2}:\d{2}$/.test(str);
}

export const getLocalTimeZone = (): TimeZoneOffset => {
  const offset = new Date().getTimezoneOffset();
  const sign = offset > 0 ? '-' : '+';
  const absOffset = Math.abs(offset);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const minutes = String(absOffset % 60).padStart(2, '0');
  const timeZone = `${sign}${hours}:${minutes}`;
  if (!isTimeZoneOffset(timeZone)) {
    throw new Error(`Invalid time zone offset: ${timeZone}`);
  }
  return timeZone;
};

export const formatDateDivider = (date: string | Date) => {
  const messageDate = new Date(date);
  const today = new Date();
  const yesterday = subDays(new Date(), 1);

  if (isSameDay(messageDate, today)) {
    return 'Today';
  } else if (isSameDay(messageDate, yesterday)) {
    return 'Yesterday';
  } else {
    return format(messageDate, 'd MMMM, yyyy');
  }
};

export function getDateYearsFromNow(years: number) {
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() + years);
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

export const formatMeetingDuration = (
  startTime: string,
  endTime: string | null,
): string => {
  if (!endTime) return '';

  const totalSeconds = differenceInSeconds(
    parseISO(endTime),
    parseISO(startTime),
  );
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours}hr${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes}min${minutes !== 1 ? 's' : ''}`);
  if (seconds > 0 && days === 0)
    parts.push(`${seconds}sec${seconds !== 1 ? 's' : ''}`);

  return parts.join(' ') || '0min';
};

export const formatMeetingTimeRange = (
  startTime: string,
  endTime: string,
): string => {
  const start = parseISO(startTime);
  const end = parseISO(endTime);

  if (isSameDay(start, end)) {
    return `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`;
  }

  if (isSameMonth(start, end)) {
    return `${format(start, 'MMM d')} – ${format(end, 'd, yyyy')}`;
  }

  if (isSameYear(start, end)) {
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
  }

  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
};

/**
 * Returns local midnight of the instant's **UTC** calendar day (its UTC
 * year/month/day), discarding the time-of-day and any timezone offset.
 *
 * Use it to read an all-day value the backend stores as a UTC instant, so it
 * renders as the same calendar day in every timezone.
 *
 * @example
 * toUtcCalendarDay(new Date('2026-03-10T23:30:00Z')); // local 2026-03-10 00:00
 */
export const toUtcCalendarDay = (date: Date): Date =>
  new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/**
 * Inverse of {@link toUtcCalendarDay}: takes the **local** calendar day of the
 * given date and returns the UTC-midnight instant of that same day.
 *
 * Use it to write an all-day value picked in the UI. This avoids the "-1 day"
 * drift for UTC+ offsets, which `setUTCHours(0, 0, 0, 0)` doesn't do since it
 * keeps the instant's UTC day.
 *
 * @example
 * // local 2026-03-10 00:30 at UTC+1
 * toUtcMidnightOfLocalDay(date); // 2026-03-10T00:00:00.000Z
 * // date.setUTCHours(0, 0, 0, 0) would give 2026-03-09T00:00:00.000Z
 */
export const toUtcMidnightOfLocalDay = (date: Date): Date =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

/**
 * Same as {@link toUtcMidnightOfLocalDay} but for the inclusive end of the
 * all-day span: 23:59:59 UTC (milliseconds zeroed) of the date's local
 * calendar day.
 */
export const toUtcEndOfLocalDay = (date: Date): Date =>
  new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59),
  );
